/**
 * PortalSidebar — the shared, capability-driven sidebar used by every portal
 * (admin dashboard + client portal). Each portal supplies only its DATA:
 *   - `groups`: nav items (each optionally gated by a `module.*` capability)
 *   - `header`: brand block shown when expanded
 *   - `footer`: optional footer block
 *
 * Visibility is DB-driven: an item with a `capability` is hidden unless the
 * signed-in user holds it (role_capabilities, via usePermissions). This is the
 * single place that gates navigation for all portals — add a route to a role in
 * the permissions matrix and it appears here, no code change.
 *
 * Rendering (colorful pills, collapse, grouped "More" divider, active
 * indicator, collapsed tooltips) is shared so the portals look identical.
 */
import { useId, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermissions } from "@/contexts/PermissionsContext";
import type { CapabilityKey } from "@/lib/permissions/capabilities";

export interface PortalNavItem {
  key: string;
  path: string;
  label: string;
  icon: React.ElementType;
  /** Tailwind colour class for the icon (e.g. "text-icon-tasks"). */
  iconClass?: string;
  /** When set, the item is hidden unless the user holds this capability. */
  capability?: CapabilityKey;
  archived?: boolean;
}

export interface PortalNavGroup {
  /** Divider label shown above the group (e.g. "More"). Omit for the first. */
  label?: string;
  items: PortalNavItem[];
}

interface PortalSidebarProps {
  groups: PortalNavGroup[];
  /** Brand block, shown only when expanded. */
  header: React.ReactNode;
  footer?: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  /** Start collapsed (icons only). */
  defaultCollapsed?: boolean;
  /** An icon rail that never expands: no toggle, and `header` shows in the collapsed rail. */
  railOnly?: boolean;
}

export function PortalSidebar({
  groups,
  header,
  footer,
  ariaLabel = "Primary",
  className,
  defaultCollapsed = false,
  railOnly = false,
}: PortalSidebarProps) {
  const [collapsedState, setCollapsed] = useState(defaultCollapsed);
  const collapsed = railOnly || collapsedState;
  const location = useLocation();
  const navigate = useNavigate();
  const { userHasPermission } = usePermissions();
  const activeIndicatorId = useId();

  /** Capability-filter every group (drop now-empty groups). */
  const visibleGroups = useMemo(
    () =>
      groups
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (it) => !it.capability || userHasPermission(it.capability),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [groups, userHasPermission],
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  let runningIndex = 0;
  const renderItem = (item: PortalNavItem) => {
    const Icon = item.icon;
    const index = runningIndex++;
    const active = isActive(item.path);

    if (item.archived) {
      return (
        <Tooltip key={item.key} open={collapsed ? undefined : false}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              disabled
              className={cn(
                "w-full h-10 rounded-xl opacity-40 cursor-not-allowed text-muted-foreground",
                collapsed ? "justify-center px-0" : "justify-start gap-3 px-3",
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </Button>
          </TooltipTrigger>
          {collapsed && (
            <TooltipContent side="right" className="font-medium">
              {item.label} (Archived)
            </TooltipContent>
          )}
        </Tooltip>
      );
    }

    return (
      <Tooltip key={item.key} open={collapsed ? undefined : false}>
        <TooltipTrigger asChild>
          <motion.button
            type="button"
            onClick={() => navigate(item.path)}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, delay: index * 0.02, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            aria-current={active ? "page" : undefined}
            className={cn(
              "group w-full h-10 flex items-center rounded-xl transition-colors duration-200",
              collapsed ? "justify-center px-0" : "justify-start gap-3 px-3",
              active
                ? "bg-sidebar-active font-semibold text-sidebar-active-foreground"
                : "font-medium text-sidebar-foreground hover:bg-sidebar-hover hover:text-foreground",
            )}
          >
            <Icon
              className={cn(
                "h-5 w-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110",
                item.iconClass,
              )}
            />
            <span
              className={cn(
                "truncate text-sm transition-all duration-200",
                collapsed ? "w-0 opacity-0" : "w-auto opacity-100",
              )}
            >
              {item.label}
            </span>
            {active && !collapsed && (
              <motion.span
                layoutId={`${activeIndicatorId}-active`}
                className="ml-auto h-5 w-1 rounded-full bg-primary"
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              />
            )}
          </motion.button>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "relative flex h-full flex-col border-r border-border bg-sidebar text-sidebar-foreground transition-[width] duration-300",
          collapsed ? "w-16" : "w-64",
          className,
        )}
      >
        {/* Header — collapse toggle + brand */}
        <div
          className={cn(
            "flex h-16 items-center gap-3 border-b border-border px-3",
            collapsed ? "justify-center" : "justify-start",
          )}
        >
          {railOnly ? (
            header
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 flex-shrink-0 rounded-xl text-sidebar-foreground hover:bg-sidebar-hover transition-all duration-200"
                onClick={() => setCollapsed((c) => !c)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <Menu className={cn("h-5 w-5 transition-transform duration-300", !collapsed && "rotate-90")} />
              </Button>
              <div
                className={cn(
                  "min-w-0 overflow-hidden transition-all duration-200",
                  collapsed ? "w-0 opacity-0" : "w-auto opacity-100",
                )}
              >
                {header}
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav aria-label={ariaLabel} className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          {visibleGroups.map((group, gi) => (
            <div key={group.label ?? `group-${gi}`} className="space-y-1">
              {group.label && (
                <div className={cn("flex items-center gap-2 px-1 pt-4 pb-1", collapsed && "justify-center")}>
                  {!collapsed && (
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </span>
                  )}
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              {group.items.map(renderItem)}
            </div>
          ))}
        </nav>

        {/* Footer */}
        {footer && !collapsed && (
          <div className="border-t border-border p-3">{footer}</div>
        )}
      </div>
    </TooltipProvider>
  );
}

export default PortalSidebar;
